import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WHATSAPP_URL = "https://wa.me/590690901241?text=" + encodeURIComponent("Bonjour Karine ✦ Je parcours votre belle boutique KARYNA et j'aurais une petite question. Merci beaucoup !");

export const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24M8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.39-.12-.56.13-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01" />
  </svg>
);

const Field = ({ label, textarea, ...props }) => (
  <label className="block">
    {textarea ? (
      <textarea
        {...props}
        rows={5}
        placeholder={label}
        className="w-full bg-white border border-transparent focus:border-gold outline-none px-4 py-3 text-sm font-light text-choco placeholder:text-choco/55 transition-colors resize-none"
      />
    ) : (
      <input
        {...props}
        placeholder={label}
        className="w-full bg-white border border-transparent focus:border-gold outline-none px-4 py-3.5 text-sm font-light text-choco placeholder:text-choco/55 transition-colors"
      />
    )}
  </label>
);

export default function Contact() {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch {
      setError("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-choco/10 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" data-testid="contact-logo" className="font-serif text-xl tracking-[0.25em] text-choco">KARYNA</Link>
          <Link to="/" data-testid="contact-back" className="text-xs uppercase tracking-[0.2em] text-choco/75 hover:text-gold transition-colors">
            ← Retour à la boutique
          </Link>
        </div>
      </header>

      <main data-testid="contact-page">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mx-auto px-6 pt-16 pb-14"
        >
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-choco">Contact</h1>
          <p className="mt-6 text-base font-light leading-relaxed text-choco">
            Bonjour, n'hésitez pas à nous contacter si vous avez la moindre question.
          </p>
          <p className="mt-3 text-base font-light leading-relaxed text-choco">
            Notre assistance se fera un plaisir de vous répondre du lundi au vendredi, de 9 h à 18 h.
          </p>
        </motion.section>

        <section className="bg-denim py-14 mt-14">
          <div className="max-w-3xl mx-auto px-6">
            {!done ? (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-4">
                <Field label="Nom" required value={form.nom} onChange={set("nom")} data-testid="contact-nom" />
                <Field label="E-mail *" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" />
                <Field label="Numéro de téléphone" type="tel" value={form.telephone} onChange={set("telephone")} data-testid="contact-telephone" />
                <Field label="Commentaire" textarea required value={form.message} onChange={set("message")} data-testid="contact-message" />
                {error && <p data-testid="contact-error" className="text-sm text-gold">{error}</p>}
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="bg-white text-denim text-xs uppercase tracking-[0.25em] px-12 py-4 hover:bg-gold hover:text-ivory transition-colors duration-400 disabled:opacity-50"
                >
                  {loading ? "Envoi en cours…" : "Envoyer"}
                </button>
              </form>
            ) : (
              <div data-testid="contact-success" className="text-center py-10">
                <p className="text-gold text-3xl">✦</p>
                <h2 className="font-serif text-2xl font-light text-ivory mt-4">Merci pour votre message</h2>
                <p className="mt-3 text-sm font-light text-ivory/80">
                  Il est bien arrivé entre nos mains. Karine vous répondra personnellement, du lundi au vendredi, de 9 h à 18 h.
                </p>
                <Link to="/" data-testid="contact-success-back" className="inline-block mt-8 border border-ivory/50 text-ivory text-xs uppercase tracking-[0.25em] px-10 py-3 hover:border-gold hover:text-gold transition-colors duration-300">
                  Retour à la boutique
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 py-16" data-testid="contact-infos">
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-choco">Quelques infos</h2>
          <div className="mt-8 space-y-3 text-base font-light text-choco/90">
            <p className="text-xs uppercase tracking-[0.25em] text-choco/85">Adresse mail</p>
            <p><a data-testid="contact-info-email" href="mailto:karine.boutiqueenligne971@gmail.com" className="hover:text-denim transition-colors">karine.boutiqueenligne971@gmail.com</a></p>
            <p className="text-xs uppercase tracking-[0.25em] text-choco/85">Numéro de téléphone</p>
            <p><a data-testid="contact-info-phone" href="tel:+590590105110" className="hover:text-denim transition-colors">00590 590 10 51 10</a></p>
          </div>
          <a
            data-testid="contact-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 flex items-center justify-between gap-6 border border-[#25D366]/40 bg-[#25D366]/5 px-6 py-5 hover:bg-[#25D366]/10 transition-colors duration-300 group"
          >
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shrink-0">
                <WhatsAppIcon size={22} />
              </span>
              <div>
                <p className="font-serif text-lg text-choco">Une question pressée ?</p>
                <p className="text-sm font-light text-choco">Écrivez-nous sur WhatsApp Business — réponse encore plus rapide.</p>
              </div>
            </div>
            <span className="text-[#128C4B] text-xs uppercase tracking-[0.2em] whitespace-nowrap group-hover:text-[#25D366] transition-colors">Ouvrir →</span>
          </a>
          <div
            data-testid="contact-brand"
            className="mt-10 border border-choco/10 h-72 flex flex-col items-center justify-center"
            style={{ background: "radial-gradient(ellipse 65% 70% at 50% 50%, rgba(201, 161, 92, 0.14), transparent 70%), #F8F4EC" }}
          >
            <p className="font-serif text-4xl sm:text-5xl tracking-[0.3em] text-gold pl-[0.3em]">KARYNA</p>
            <p className="mt-4 font-accent italic text-base sm:text-lg text-gold/85">
              Bien-être ✦ Mouvement ✦ Style ✦ Voyage ✦ Décoration
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
